import {
  BookOpen, Languages, AtSign, NotebookPen, HeartHandshake, Sunrise, Compass, HelpCircle,
  Smartphone, GraduationCap, Video, CreditCard, MessagesSquare, ShieldCheck,
} from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";

const GROUP_A = [
  { icon: BookOpen, title: "Bible reader with word study", body: "Tap any word for Greek, Strong's, morphology, and definitions. Seminary-grade tools, free." },
  { icon: HelpCircle, title: "QuestionGod Integration", body: "Tap the question icon on any verse to see who asked, who it was asked to, its purpose, the response, commentary, and practical application." },
  { icon: Languages, title: "Compare translations", body: "Read and compare BSB, WEB, and KJV at launch, with more coming." },
  { icon: AtSign, title: "@ Scripture engine", body: "Type @ anywhere to search by reference (@matthew1:3, typos welcome) or phrase (@clothing white as snow) and insert verses as living cards." },
  { icon: NotebookPen, title: "Prayer journal & notes", body: "Capture what God is saying, with categories you create." },
  { icon: HeartHandshake, title: "Praying-for list", body: "Carry people, situations, and burdens, and watch answered prayers become testimonies." },
  { icon: Sunrise, title: "Practices", body: "Gratitude, prayer, and fasting: a morning prompt, offline practice, and evening reflection. No streaks. No guilt. Ever." },
  { icon: Compass, title: "Goal tracking with biblical encouragement", body: "Every goal is paired with Scripture and biblically based encouragement, so growth in health, stewardship, learning, and calling is pursued with the Word attached." },
];

const GROUP_B = [
  { icon: Smartphone, title: "Community in the app", body: "Your community lives inside the Word Amp app on iOS, Android, and web. Add your own fully branded app as a paid upgrade." },
  { icon: GraduationCap, title: "Courses & digital products", body: "Courses, cohorts, and digital products with no platform fees. Payments run on your own Stripe account." },
  { icon: Video, title: "Live streaming & events", body: "Live streaming, events, and group video sessions." },
  { icon: CreditCard, title: "Memberships & payments", body: "Memberships, subscriptions, and one-time payments." },
  { icon: MessagesSquare, title: "Real-time community", body: "Real-time community feeds, chat, and groups." },
  { icon: ShieldCheck, title: "Safe by design", body: "Every community on the platform shares the same foundation." },
];

const FeatureRow = ({ icon: Icon, title, body, dark }) => (
  <div className="flex gap-5 py-6 border-b border-ink/10 last:border-0">
    <div className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center ${dark ? "bg-green/15" : "bg-green/10"}`}>
      <Icon className="w-5 h-5 text-green-dark" strokeWidth={1.75} />
    </div>
    <div>
      <h3 className="font-serif text-xl text-ink mb-1.5">{title}</h3>
      <p className="text-ink/70 leading-relaxed">{body}</p>
    </div>
  </div>
);

export const Features = () => (
  <section id="features" className="relative py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Group A */}
      <Reveal>
        <div className="flex items-baseline gap-4 mb-3">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-green-dark">Formation Tools</p>
          <span className="text-xs font-medium text-ink/50 rounded-full bg-green/10 px-3 py-1">Live in demo</span>
        </div>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl text-balance">
          Scripture In Every Field You Touch.
        </h2>
      </Reveal>

      <div className="mt-12 grid md:grid-cols-2 gap-x-14 gap-y-0 bg-paperwhite rounded-3xl border border-ink/5 shadow-sm p-8 md:p-12">
        {GROUP_A.map((f, i) => (
          <Reveal key={f.title} delay={(i % 2) * 0.06}>
            <FeatureRow {...f} />
          </Reveal>
        ))}
      </div>

      {/* Group B */}
      <Reveal>
        <div className="flex items-baseline gap-4 mb-3 mt-16 md:mt-24">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-plum">Your Community</p>
          <span className="text-xs font-medium text-plum rounded-full bg-plum/10 px-3 py-1">Coming soon</span>
        </div>
        <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-ink max-w-2xl text-balance">
          The Community Platform, Built On The Same Foundation.
        </h2>
      </Reveal>

      <div className="mt-12 grid md:grid-cols-2 gap-x-14 gap-y-0 bg-paperwhite rounded-3xl border border-ink/5 shadow-sm p-8 md:p-12">
        {GROUP_B.map((f, i) => (
          <Reveal key={f.title} delay={(i % 2) * 0.06}>
            <FeatureRow {...f} dark />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
